import { logger } from '@utils/safeLogger'
// import { InsuranceData } from '@dataTypes/insurance.types'
import {
  Attachment,
  Procedure,
  ProcedureAttachment,
} from '@dataTypes/claimFeature.types'
import axios from './axiosConfig'

import { getEnv } from '@utils/env'
import { isUUID } from '@utils/claims.util'
import { toApiError } from './errors/api-error'

// Constants
const BUSINESS_LAYER_API = getEnv('REACT_APP_BUSINESS_LAYER_CLAIMS', '')
const EXTERNAL_LAYER_API = getEnv('REACT_APP_EXTERNAL_LAYER_CLAIMS', '')

const headers = {
  'Content-Type': 'application/json',
}

// Types
interface ApiResponse<T> {
  statusCode: number
  message: string
  data: T
}

type ClaimsResponseData = any

export type GetClaims = ApiResponse<ClaimsResponseData>

export type GetClaimsByPatientIdResponse = ApiResponse<ClaimsResponseData>

export type CreateClaimByPatientIdResponse = ApiResponse<ClaimsResponseData>

export type DeleteClaimByIdResponse = ApiResponse<ClaimsResponseData>

const appendFormClaimsData = (formData: FormData, claimsData: any): void => {
  for (const key in claimsData) {
    if (
      key !== 'files' &&
      Object.prototype.hasOwnProperty.call(claimsData, key)
    ) {
      const value = claimsData[key]
      formData.append(
        key,
        typeof value === 'object' ? JSON.stringify(value) : value,
      )
    }
  }

  if (claimsData.attachments && claimsData.attachments.length > 0) {
    claimsData.attachments.forEach((attachment: Attachment) => {
      if (attachment.newFile) {
        formData.append(
          'files',
          attachment.newFile,
          `${attachment.mapId}_${attachment.fileName}`,
        )
      }
    })
  }

  if (claimsData.procedures && claimsData.procedures.length > 0) {
    claimsData.procedures.forEach((procedure: Procedure) => {
      procedure.procedureAttachments.forEach(
        (attachment: ProcedureAttachment) => {
          if (attachment.newFile) {
            formData.append(
              'files',
              attachment.newFile,
              `${attachment.mapId}_${attachment.fileName}`,
            )
          }
        },
      )
    })
  }
}

export const createClaimByPatientId = async (
  patientId: string,
  claimsData: any,
): Promise<any> => {
  try {
    const formData = new FormData()
    appendFormClaimsData(formData, claimsData)

    const response = await axios.post<CreateClaimByPatientIdResponse>(
      `${BUSINESS_LAYER_API}/api/v1/claims/${patientId}`,
      formData,
      {
        headers: {
          'Content-Type': undefined,
        },
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in CreateClaimByPatientId:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const updateClaimById = async (
  claimId: string,
  claimsData: any,
): Promise<any> => {
  try {
    const formData = new FormData()
    appendFormClaimsData(formData, claimsData)

    const response = await axios.put<CreateClaimByPatientIdResponse>(
      `${BUSINESS_LAYER_API}/api/v1/claims/update/${claimId}`,
      formData,
      {
        headers: {
          'Content-Type': undefined,
        },
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in updateClaimByPatientId:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const updateClaimStatusById = async (
  claimId: string,
  claimsData: any,
): Promise<any> => {
  try {
    const formData = new FormData()
    appendFormClaimsData(formData, claimsData)

    const response = await axios.put<CreateClaimByPatientIdResponse>(
      `${BUSINESS_LAYER_API}/api/v1/claims/update-status/${claimId}`,
      formData,
      {
        headers: {
          'Content-Type': undefined,
        },
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in updateClaimByPatientId:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const deleteClaimById = async (claimId: string): Promise<any> => {
  try {
    const response = await axios.delete<CreateClaimByPatientIdResponse>(
      `${BUSINESS_LAYER_API}/api/v1/claims/delete/${claimId}`,
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in deleteClaimById:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const getClaims = async (patientId: string): Promise<GetClaims> => {
  try {
    const response = await axios.get<GetClaims>(
      `${BUSINESS_LAYER_API}/api/v1/claims${patientId ? `?patientId=${patientId}` : ''}`,
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in getClaims:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const submitClaimExternal = async (formData: any): Promise<any> => {
  try {
    const response = await axios.post<any>(
      `${EXTERNAL_LAYER_API}/api/v1/external/claims/add`,
      formData,
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in submitClaim:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const getClaimStatusExternal = async (formData: any): Promise<any> => {
  try {
    const response = await axios.post<any>(
      `${EXTERNAL_LAYER_API}/api/v1/external/claims/check`,
      formData,
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in getClaimStatusExternal:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const saveDraftClaim = async (claimsData: any): Promise<any> => {
  try {
    const formData = new FormData()
    appendFormClaimsData(formData, claimsData)

    formData.append('draft_patient_id', claimsData.patientIdSystem || '')
    formData.append(
      'draft_claim_id',
      isUUID(claimsData.id) ? claimsData.id : '',
    )

    const response = await axios.post<CreateClaimByPatientIdResponse>(
      `${BUSINESS_LAYER_API}/api/v1/claims/save-draft`,
      formData,
      {
        headers: {
          'Content-Type': undefined,
        },
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in saveDraftClaim:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}
